// components/admin/ProductsTab.js - VERSIONE COMPLETA E CORRETTA con bottone "occhio" funzionante
import React, { useState, useEffect, useCallback } from "react"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import {
  fetchProductStructure,
  fetchCategory,
  addCategory,
  updateCategory,
  deleteCategory,
  addFamily,
  updateFamily,
  deleteFamily,
  addProduct,
  updateProduct,
  deleteProduct,
  reorderCategories,
  reorderFamilies,
  reorderProducts,
  fetchProducts,
} from "../../api"
import CategoryForm from "./CategoryForm"
import FamilyForm from "./FamilyForm"
import ProductForm from "./ProductForm"
import ConfirmationModal from "./ConfirmationModal"
import ProductsManagerModal from "./ProductsManagerModal"
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiChevronRight,
  FiChevronDown,
  FiGrid,
  FiEye,
  FiTag,
  FiDollarSign,
  FiCode,
  FiMove,
  FiCopy,
} from "react-icons/fi"

const ProductsTab = ({
  products,
  setProducts,
  categories,
  setCategories,
  onProductChange,
  setLoading,
}) => {
  const [productStructure, setProductStructure] = useState({})
  const [expandedCategories, setExpandedCategories] = useState({})
  const [expandedFamilies, setExpandedFamilies] = useState({})
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedFamily, setSelectedFamily] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [showCategoryForm, setShowCategoryForm] = useState(false)
  const [showFamilyForm, setShowFamilyForm] = useState(false)
  const [showProductForm, setShowProductForm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [familyDataForForm, setFamilyDataForForm] = useState(null)
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  })
  const [showProductsManager, setShowProductsManager] = useState(false)
  const [productsToManage, setProductsToManage] = useState([])
  const [manageContext, setManageContext] = useState({
    category: "",
    family: "",
  })

  const loadAccordionStates = () => {
    try {
      const savedExpandedCategories = localStorage.getItem(
        "admin-expanded-categories"
      )
      const savedExpandedFamilies = localStorage.getItem(
        "admin-expanded-families"
      )

      if (savedExpandedCategories) {
        setExpandedCategories(JSON.parse(savedExpandedCategories))
      }
      if (savedExpandedFamilies) {
        setExpandedFamilies(JSON.parse(savedExpandedFamilies))
      }
    } catch (error) {
      console.warn("Error loading accordion states:", error)
    }
  }

  // Save accordion states to localStorage
  const saveAccordionStates = () => {
    try {
      localStorage.setItem(
        "admin-expanded-categories",
        JSON.stringify(expandedCategories)
      )
      localStorage.setItem(
        "admin-expanded-families",
        JSON.stringify(expandedFamilies)
      )
    } catch (error) {
      console.warn("Error saving accordion states:", error)
    }
  }

  useEffect(() => {
    const initializeAndLoad = async () => {
      await loadProductStructure()
      loadAccordionStates()
    }
    initializeAndLoad()
  }, [])

  const loadProductStructure = async () => {
    setLoading(true)
    try {
      const structure = await fetchProductStructure()
      setProductStructure(structure)
      setExpandedCategories((prev) => {
        const updated = { ...prev }
        for (const cat of Object.keys(structure)) {
          if (updated[cat] === undefined) updated[cat] = false
        }
        for (const cat of Object.keys(updated)) {
          if (!structure[cat]) delete updated[cat]
        }
        return updated
      })
      setExpandedFamilies((prev) => {
        const updated = { ...prev }
        for (const cat of Object.keys(structure)) {
          for (const fam of structure[cat]) {
            const key = `${cat}-${fam}`
            if (updated[key] === undefined) updated[key] = false
          }
        }
        for (const key of Object.keys(updated)) {
          const [cat, fam] = key.split("-")
          if (!structure[cat] || !structure[cat].includes(fam)) {
            delete updated[key]
          }
        }
        return updated
      })
      setCategories(Object.keys(structure))
      return structure // Restituisce la struttura per poterla usare subito
    } catch (error) {
      console.error("Failed to load product structure:", error)
      return {}
    } finally {
      setLoading(false)
    }
  }

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => {
      const newState = { ...prev, [category]: !prev[category] }
      // Save to localStorage
      setTimeout(() => {
        try {
          localStorage.setItem(
            "admin-expanded-categories",
            JSON.stringify(newState)
          )
        } catch (error) {
          console.warn("Error saving category state:", error)
        }
      }, 0)
      return newState
    })
  }

  const toggleFamily = (category, family) => {
    const key = `${category}-${family}`
    setExpandedFamilies((prev) => {
      const newState = { ...prev, [key]: !prev[key] }
      // Save to localStorage
      setTimeout(() => {
        try {
          localStorage.setItem(
            "admin-expanded-families",
            JSON.stringify(newState)
          )
        } catch (error) {
          console.warn("Error saving family state:", error)
        }
      }, 0)
      return newState
    })
  }

  const handleAddCategory = () => {
    setIsEditing(false)
    setSelectedCategory(null)
    setShowCategoryForm(true)
  }

  const handleEditCategory = (category) => {
    setIsEditing(true)
    setSelectedCategory(category)
    setShowCategoryForm(true)
  }

  const handleSaveCategory = async (categoryData) => {
    setLoading(true)
    try {
      if (isEditing) {
        await updateCategory(selectedCategory, categoryData.name)
      } else {
        await addCategory(categoryData.name)
      }
      await loadProductStructure()
      setShowCategoryForm(false)
    } catch (error) {
      console.error("Failed to save category:", error)
      alert(`Error: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmDeleteCategory = (category) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Category",
      message: `Are you sure you want to delete "${category}" and all its content?`,
      onConfirm: () => handleDeleteCategory(category),
    })
  }

  const handleDeleteCategory = async (category) => {
    setLoading(true)
    try {
      await deleteCategory(category)
      await loadProductStructure()
      setConfirmModal({ isOpen: false })
    } catch (error) {
      console.error("Failed to delete category:", error)
      alert(`Error: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleAddFamily = (category) => {
    setIsEditing(false)
    setSelectedCategory(category)
    setSelectedFamily(null)
    setFamilyDataForForm(null)
    setShowFamilyForm(true)
  }

  const handleEditFamily = async (category, family) => {
    setLoading(true)
    try {
      const categoryData = await fetchCategory(category)
      const familyObject = categoryData?.families?.[family]
      if (!familyObject) throw new Error(`Family "${family}" not found`)
      setIsEditing(true)
      setSelectedCategory(category)
      setSelectedFamily(family)
      setFamilyDataForForm(familyObject)
      setShowFamilyForm(true)
    } catch (error) {
      console.error("Failed to load family for editing:", error)
      alert(`Error loading family data: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveFamily = async (familyData) => {
    setLoading(true)
    try {
      if (isEditing) {
        await updateFamily(
          selectedCategory,
          selectedFamily,
          familyData.name,
          familyData
        )
      } else {
        await addFamily(selectedCategory, familyData.name, familyData)
      }
      await loadProductStructure()
      setShowFamilyForm(false)
    } catch (error) {
      console.error("Failed to save family:", error)
      alert(`Error: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmDeleteFamily = (category, family) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Family",
      message: `Are you sure you want to delete the family "${family}"?`,
      onConfirm: () => handleDeleteFamily(category, family),
    })
  }

  const handleDeleteFamily = async (category, family) => {
    setLoading(true)
    try {
      await deleteFamily(category, family)
      await loadProductStructure()
      setConfirmModal({ isOpen: false })
    } catch (error) {
      console.error("Failed to delete family:", error)
      alert(`Error: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleAddProduct = async (category, family) => {
    setLoading(true)
    try {
      const categoryData = await fetchCategory(category)
      const familyObject = categoryData?.families?.[family]
      if (!familyObject) throw new Error("Family data not found")
      setIsEditing(false)
      setSelectedCategory(category)
      setSelectedFamily(family)
      setSelectedProduct(null)
      setFamilyDataForForm(familyObject)
      setShowProductForm(true)
    } catch (error) {
      alert(`Error preparing product form: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleEditProduct = async (product) => {
    setLoading(true)
    try {
      const categoryData = await fetchCategory(product.category)
      const familyObject = categoryData?.families?.[product.family]
      if (!familyObject) throw new Error("Family data not found")
      setIsEditing(true)
      setSelectedCategory(product.category)
      setSelectedFamily(product.family)
      setSelectedProduct(product)
      setFamilyDataForForm(familyObject)
      setShowProductForm(true)
    } catch (error) {
      alert(`Error preparing product form: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveProduct = async (productData) => {
    setLoading(true)
    try {
      console.log("🔧 ProductsTab handleSaveProduct received:", productData)
      console.log("🔧 Category/Family check:", {
        receivedCategory: productData.category,
        receivedFamily: productData.family,
        isEditing: isEditing,
        selectedProduct: selectedProduct?.code,
      })

      let dataToSend = { ...productData }

      // SE È UNA MODIFICA, AGGIUNGI IL CODICE VECCHIO
      if (isEditing && selectedProduct) {
        console.log("🔧 Original product data:", {
          originalCategory: selectedProduct.category,
          originalFamily: selectedProduct.family,
          originalCode: selectedProduct.code,
          newCode: productData.code,
          newCategory: productData.category,
          newFamily: productData.family,
        })

        // 👇 BLOCCO CRITICO 👇
        // Se il codice è cambiato, aggiungi ENTRAMBI
        if (selectedProduct.code !== productData.code) {
          dataToSend.originalCode = selectedProduct.code
          console.log("🔧 ⚠️ CODE CHANGED! Sending:", {
            searchingFor: selectedProduct.code,
            newCode: productData.code,
          })
        }
        // 👆 FINE BLOCCO 👆
      }

      console.log("🔧 Final data being sent to API:", dataToSend)

      if (isEditing) {
        await updateProduct(dataToSend)
      } else {
        await addProduct(dataToSend)
      }

      await refreshGlobalProducts()
      setShowProductForm(false)
    } catch (error) {
      console.error("Failed to save product:", error)
      alert(`Error: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmDeleteProduct = (product) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Product",
      message: `Are you sure you want to delete "${getDisplayName(product)}"?`,
      onConfirm: () => handleDeleteProduct(product),
    })
  }

  const handleDeleteProduct = async (product) => {
    setLoading(true)
    try {
      await deleteProduct(product.code)
      await refreshGlobalProducts() // <-- Chiamata chiave
      setConfirmModal({ isOpen: false })
    } catch (error) {
      console.error("Failed to delete product:", error)
      alert(`Error: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  // ==================================================================
  // ===== CORREZIONE CHIAVE: Funzione di refresh più robusta =====
  // ==================================================================
  const refreshGlobalProducts = async () => {
    try {
      // 1. Ricarica la struttura per essere sicuro di avere l'ultima versione
      const newStructure = await loadProductStructure()

      if (!newStructure || Object.keys(newStructure).length === 0) {
        console.warn("Empty product structure received")
        return
      }

      // 2. Ricarica tutti i prodotti basandoti sulla nuova struttura
      const categoryNames = Object.keys(newStructure)
      const results = []
      const failedCategories = []

      // Fetch categories with proper error tracking
      for (const categoryName of categoryNames) {
        try {
          const categoryData = await fetchCategory(categoryName)
          results.push({ categoryName, categoryData, success: true })
        } catch (e) {
          console.error(`Failed to fetch products for ${categoryName}:`, e)
          failedCategories.push(categoryName)
          results.push({ categoryName, categoryData: null, success: false })
        }
      }

      // If ALL categories failed, don't update state (keep current data)
      if (failedCategories.length === categoryNames.length) {
        console.error("All category fetches failed - keeping current state")
        alert("Errore nel caricamento dei prodotti. Riprova o ricarica la pagina.")
        return
      }

      // If SOME categories failed, warn but continue with partial data
      if (failedCategories.length > 0) {
        console.warn(`Some categories failed to load: ${failedCategories.join(", ")}`)
        // Don't alert for partial failures - just log
      }

      const allProducts = []
      results.forEach(({ categoryName, categoryData, success }) => {
        if (!success || !categoryData || !categoryData.families) {
          return // Skip failed categories
        }

        // Iterate over families with proper name tracking
        Object.entries(categoryData.families).forEach(([familyName, family]) => {
          if (family.products && Array.isArray(family.products)) {
            // Aggiungi categoria e famiglia a ogni prodotto
            const productsWithMeta = family.products.map((p) => ({
              ...p,
              category: categoryName,
              family: familyName,
            }))
            allProducts.push(...productsWithMeta)
          }
        })
      })

      // Validate we're not losing all products
      const currentProductCount = products.length
      if (currentProductCount > 0 && allProducts.length === 0) {
        console.error("Refresh would result in 0 products - aborting state update")
        alert("Errore: il refresh ha restituito 0 prodotti. Ricarica la pagina.")
        return
      }

      // Warn if significant product loss (more than 20% missing)
      if (currentProductCount > 0 && allProducts.length < currentProductCount * 0.8) {
        console.warn(`Significant product count change: ${currentProductCount} -> ${allProducts.length}`)
      }

      setProducts(allProducts)
      console.log(
        `Refreshed global state: ${allProducts.length} total products`
      )
    } catch (error) {
      console.error("Failed to refresh global products:", error)
      alert("Errore nel refresh dei prodotti. Ricarica la pagina se necessario.")
    }
  }

  const getDisplayName = (product) => {
    return product?.lang?.it || product?.name || ""
  }

  const handleOpenProductsManager = (category, family) => {
    const familyProducts = getProductsForFamily(category, family)
    setProductsToManage(familyProducts)
    setManageContext({ category, family })
    setShowProductsManager(true)
  }

  const handleProductsUpdated = async () => {
    setLoading(true)
    try {
      await refreshGlobalProducts()
      if (manageContext.category && manageContext.family) {
        const updatedFamilyProducts = getProductsForFamily(
          manageContext.category,
          manageContext.family
        )
        setProductsToManage(updatedFamilyProducts)
      }
    } catch (error) {
      console.error("Failed to refresh after batch update:", error)
    } finally {
      setLoading(false)
    }
  }

  const getProductsForFamily = (categoryName, familyName) => {
    return products.filter(
      (p) => p.category === categoryName && p.family === familyName
    )
  }

  const handleViewProduct = async (productOrCode) => {
    setLoading(true)
    try {
      let productCode, productObj

      // Gestisce sia oggetti prodotto che codici stringa
      if (typeof productOrCode === "string") {
        productCode = productOrCode
        // Trova l'oggetto prodotto dalla lista
        productObj = products.find((p) => p.code === productCode)
      } else {
        productObj = productOrCode
        productCode = productObj.code
      }

      console.log(`Loading product in viewer: ${productCode}`)

      // Chiama la funzione onProductChange per caricare il prodotto nel player
      if (onProductChange && productCode) {
        onProductChange(productCode)

        // Mostra un feedback all'utente
        const displayName = productObj
          ? getDisplayName(productObj)
          : productCode
        alert(`Prodotto "${displayName}" (${productCode}) caricato nel viewer`)
      } else {
        console.error(
          "onProductChange function not available or no product code"
        )
        alert("Impossibile caricare il prodotto nel viewer")
      }
    } catch (error) {
      console.error("Failed to load product in viewer:", error)
      alert(`Errore nel caricamento del prodotto: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleDuplicateProduct = async (product) => {
    if (
      !window.confirm(
        `Sei sicuro di voler duplicare il prodotto "${product.name}"?`
      )
    ) {
      return
    }

    setLoading(true)
    try {
      const duplicatedProduct = {
        ...product,
        // MANTIENI IL CODE IDENTICO (necessario per modello 3D)
        code: product.code,
        // MANTIENI onpage_code identico
        onpage_code: product.onpage_code,
        // Solo il nome principale ha _copy
        name: `${product.name}_copy`,
        // Le traduzioni rimangono ESATTAMENTE come l'originale
        lang: product.lang ? { ...product.lang } : undefined,
      }

      await addProduct(duplicatedProduct)
      await refreshGlobalProducts()
    } catch (error) {
      console.error("Failed to duplicate product:", error)
      alert("Error duplicating product: " + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleDuplicateFamily = async (categoryName, familyName) => {
    if (
      !window.confirm(
        `Sei sicuro di voler duplicare la famiglia "${familyName}"?`
      )
    ) {
      return
    }

    const newFamilyName = `${familyName}_copy`
    setLoading(true)
    try {
      // 1. Ottieni i dati della famiglia originale
      const categoryData = await fetchCategory(categoryName)
      const familyData = categoryData?.families?.[familyName]
      if (!familyData) throw new Error("Family data not found")

      // 2. Crea la nuova famiglia SENZA prodotti inizialmente
      const familyWithoutProducts = {
        ...familyData,
        products: [],
      }

      await addFamily(categoryName, newFamilyName, familyWithoutProducts)

      // 3. Duplica tutti i prodotti mantenendo il CODE identico
      if (familyData.products && Array.isArray(familyData.products)) {
        for (const product of familyData.products) {
          const duplicatedProduct = {
            ...product,
            // MANTIENI IL CODE IDENTICO
            code: product.code,
            // MANTIENI onpage_code identico
            onpage_code: product.onpage_code,
            // Solo il nome ha _copy
            name: `${product.name}_copy`,
            category: categoryName,
            family: newFamilyName,
            // Le traduzioni rimangono identiche
            lang: product.lang ? { ...product.lang } : undefined,
          }

          await addProduct(duplicatedProduct)
        }
      }

      await loadProductStructure()
      await refreshGlobalProducts()
    } catch (error) {
      console.error("Failed to duplicate family:", error)
      alert("Error duplicating family: " + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleDuplicateCategory = async (categoryName) => {
    if (
      !window.confirm(
        `Sei sicuro di voler duplicare la categoria "${categoryName}"?`
      )
    ) {
      return
    }

    const newCategoryName = `${categoryName}_copy`
    setLoading(true)
    try {
      // 1. Ottieni tutti i dati della categoria originale
      const categoryData = await fetchCategory(categoryName)

      // 2. Crea la nuova categoria
      await addCategory(newCategoryName)

      // 3. Duplica tutte le famiglie con i loro prodotti
      if (categoryData.families) {
        for (const [familyName, familyData] of Object.entries(
          categoryData.families
        )) {
          // 3a. Crea la famiglia senza prodotti inizialmente
          const familyWithoutProducts = {
            ...familyData,
            products: [],
          }

          await addFamily(newCategoryName, familyName, familyWithoutProducts)

          // 3b. Duplica tutti i prodotti mantenendo il CODE identico
          if (familyData.products && Array.isArray(familyData.products)) {
            for (const product of familyData.products) {
              const duplicatedProduct = {
                ...product,
                // MANTIENI IL CODE IDENTICO
                code: product.code,
                // MANTIENI onpage_code identico
                onpage_code: product.onpage_code,
                // Solo il nome ha _copy
                name: `${product.name}_copy`,
                category: newCategoryName,
                family: familyName,
                // Le traduzioni rimangono identiche
                lang: product.lang ? { ...product.lang } : undefined,
              }

              await addProduct(duplicatedProduct)
            }
          }
        }
      }

      await loadProductStructure()
      await refreshGlobalProducts()
    } catch (error) {
      console.error("Failed to duplicate category:", error)
      alert("Error duplicating category: " + error.message)
    } finally {
      setLoading(false)
    }
  }

  // Helper function to safely decode droppable IDs
  // Uses "|" as separator instead of "-" to avoid conflicts with names containing hyphens
  const parseDroppableId = (droppableId, type) => {
    if (type === "PRODUCT") {
      // Format: family-CATEGORY|FAMILY (using | as safe separator)
      // But we need to handle legacy format: family-CATEGORY-FAMILY
      const match = droppableId.match(/^family-(.+)$/)
      if (!match) return { category: null, family: null }

      const rest = match[1]

      // Try new format first (with |)
      if (rest.includes("|")) {
        const [cat, fam] = rest.split("|").map(decodeURIComponent)
        return { category: cat, family: fam }
      }

      // Fallback to legacy format - find the category/family from our known structure
      // This handles cases where names contain hyphens
      for (const cat of Object.keys(productStructure)) {
        for (const fam of productStructure[cat]) {
          const encoded = `${encodeURIComponent(cat)}-${encodeURIComponent(fam)}`
          if (rest === encoded || rest === `${cat}-${fam}`) {
            return { category: cat, family: fam }
          }
        }
      }

      // Last resort: simple split (may fail with hyphens in names)
      const lastDash = rest.lastIndexOf("-")
      if (lastDash > 0) {
        return {
          category: decodeURIComponent(rest.substring(0, lastDash)),
          family: decodeURIComponent(rest.substring(lastDash + 1)),
        }
      }

      return { category: null, family: null }
    }

    if (type === "FAMILY") {
      // Format: category-CATEGORY
      const match = droppableId.match(/^category-(.+)$/)
      if (!match) return { category: null }
      return { category: decodeURIComponent(match[1]) }
    }

    return {}
  }

  const handleDragEnd = useCallback(
    async (result) => {
      const { destination, source, type, draggableId } = result

      if (
        !destination ||
        (destination.droppableId === source.droppableId &&
          destination.index === source.index)
      ) {
        return
      }

      setLoading(true)

      // SAVE ORIGINAL STATE FOR ROLLBACK
      const originalProducts = [...products]
      const originalStructure = { ...productStructure }

      try {
        // Handle PRODUCT dragging
        if (type === "PRODUCT") {
          const sourceInfo = parseDroppableId(source.droppableId, "PRODUCT")
          const destInfo = parseDroppableId(destination.droppableId, "PRODUCT")

          const sourceCatId = sourceInfo.category
          const sourceFamId = sourceInfo.family
          const destCatId = destInfo.category
          const destFamId = destInfo.family

          // Validate parsed IDs
          if (!sourceCatId || !sourceFamId || !destCatId || !destFamId) {
            console.error("Failed to parse droppable IDs:", {
              source: source.droppableId,
              destination: destination.droppableId,
              parsed: { sourceInfo, destInfo },
            })
            alert("Errore: impossibile identificare la posizione di origine o destinazione")
            return
          }

          console.log("Product drag:", {
            from: `${sourceCatId}/${sourceFamId}`,
            to: `${destCatId}/${destFamId}`,
            productId: draggableId,
          })

          const productCode = draggableId.replace("product-", "")
          const product = products.find((p) => p.code === productCode)

          if (!product) {
            console.error("Product not found:", productCode)
            alert(`Errore: prodotto ${productCode} non trovato`)
            return
          }

          // Check if moving between different families
          if (sourceCatId !== destCatId || sourceFamId !== destFamId) {
            // MOVE PRODUCT TO DIFFERENT FAMILY/CATEGORY
            console.log(
              `Moving product ${productCode} from ${sourceCatId}/${sourceFamId} to ${destCatId}/${destFamId}`
            )

            // STEP 1: Optimistic UI update IMMEDIATELY
            setProducts((prev) => {
              const filtered = prev.filter((p) => p.code !== productCode)
              return [
                ...filtered,
                { ...product, category: destCatId, family: destFamId },
              ]
            })

            // STEP 2: Call API to update product
            const updatedProduct = {
              ...product,
              category: destCatId,
              family: destFamId,
            }

            try {
              await updateProduct(updatedProduct)

              // STEP 3: Verify the move by refreshing
              await refreshGlobalProducts()

              alert(
                `Prodotto "${product.name}" spostato in ${destCatId}/${destFamId}`
              )
            } catch (apiError) {
              // ROLLBACK on API failure
              console.error("API error during product move:", apiError)
              setProducts(originalProducts)
              throw apiError
            }
          } else {
            // REORDER WITHIN SAME FAMILY
            const familyProducts = getProductsForFamily(sourceCatId, sourceFamId)
            const reorderedProducts = [...familyProducts]
            const [movedProduct] = reorderedProducts.splice(source.index, 1)
            reorderedProducts.splice(destination.index, 0, movedProduct)

            // STEP 1: Optimistic UI update
            setProducts((prevProducts) => {
              const otherProducts = prevProducts.filter(
                (p) => !(p.category === sourceCatId && p.family === sourceFamId)
              )
              return [...otherProducts, ...reorderedProducts]
            })

            // STEP 2: Call API
            const productOrder = reorderedProducts.map((p) => p.code)
            try {
              await reorderProducts(sourceCatId, sourceFamId, productOrder)
              // Don't refresh immediately - trust the optimistic update
              // Only refresh structure
              await loadProductStructure()
            } catch (apiError) {
              // ROLLBACK on API failure
              console.error("API error during reorder:", apiError)
              setProducts(originalProducts)
              throw apiError
            }
          }
        }

        // Handle FAMILY dragging
        else if (type === "FAMILY") {
          const sourceInfo = parseDroppableId(source.droppableId, "FAMILY")
          const destInfo = parseDroppableId(destination.droppableId, "FAMILY")

          const sourceCatId = sourceInfo.category
          const destCatId = destInfo.category

          // Extract family name from draggableId
          // Format: family-CATEGORY-FAMILY
          const familyMatch = draggableId.match(/^family-(.+)-([^-]+)$/)
          let decodedFamilyId

          if (familyMatch) {
            decodedFamilyId = decodeURIComponent(familyMatch[2])
          } else {
            // Fallback: find family from structure
            const families = productStructure[sourceCatId] || []
            decodedFamilyId = families[source.index]
          }

          if (!decodedFamilyId) {
            console.error("Failed to parse family ID:", draggableId)
            alert("Errore: impossibile identificare la famiglia")
            return
          }

          console.log("Family drag:", {
            family: decodedFamilyId,
            from: sourceCatId,
            to: destCatId,
          })

          // Check if moving between different categories
          if (sourceCatId !== destCatId) {
            // MOVE FAMILY TO DIFFERENT CATEGORY
            console.log(
              `Moving family ${decodedFamilyId} from ${sourceCatId} to ${destCatId}`
            )

            // Get family data from source category
            const sourceCategoryData = await fetchCategory(sourceCatId)
            const familyData = sourceCategoryData.families[decodedFamilyId]

            if (!familyData) {
              console.error("Family data not found:", decodedFamilyId)
              alert(`Errore: dati famiglia "${decodedFamilyId}" non trovati`)
              return
            }

            // CRITICAL: Verify products are included in family data
            const productCount = familyData.products?.length || 0
            console.log(`Family "${decodedFamilyId}" has ${productCount} products to move`)

            if (productCount > 0) {
              // Double-check with user before moving family with products
              if (!window.confirm(
                `Stai per spostare la famiglia "${decodedFamilyId}" con ${productCount} prodotti.\n` +
                `Da: ${sourceCatId}\n` +
                `A: ${destCatId}\n\n` +
                `Continuare?`
              )) {
                return
              }
            }

            try {
              // IMPORTANT: Ensure we're sending complete family data including products
              const completeData = {
                ...familyData,
                products: familyData.products || [],
                defaultMaterials: familyData.defaultMaterials || {},
                defaultMaterialLinks: familyData.defaultMaterialLinks || [],
                typeLinks: familyData.typeLinks || [],
                customViews: familyData.customViews || [],
                collectionTranslations: familyData.collectionTranslations || {},
              }

              console.log("Sending family data with products:", {
                family: decodedFamilyId,
                productCount: completeData.products.length,
                products: completeData.products.map(p => p.code),
              })

              // Add family to destination category FIRST
              await addFamily(destCatId, decodedFamilyId, completeData)

              // Verify the family was created with products before deleting source
              const destCategoryData = await fetchCategory(destCatId)
              const newFamilyData = destCategoryData.families?.[decodedFamilyId]
              const newProductCount = newFamilyData?.products?.length || 0

              if (productCount > 0 && newProductCount === 0) {
                // Products were lost! Don't delete source
                console.error("CRITICAL: Products were not copied to destination!")
                alert(
                  `ERRORE CRITICO: I prodotti non sono stati copiati nella destinazione.\n` +
                  `La famiglia sorgente NON è stata eliminata per sicurezza.\n` +
                  `Contatta l'amministratore.`
                )
                await loadProductStructure()
                await refreshGlobalProducts()
                return
              }

              // THEN remove from source category (only if products were preserved)
              await deleteFamily(sourceCatId, decodedFamilyId)

              // Refresh the structure
              await loadProductStructure()
              await refreshGlobalProducts()

              alert(
                `Famiglia "${decodedFamilyId}" spostata da ${sourceCatId} a ${destCatId}\n` +
                `${productCount} prodotti trasferiti con successo.`
              )
            } catch (apiError) {
              // Try to rollback - re-add to source if it was deleted
              console.error("API error during family move:", apiError)
              await loadProductStructure()
              await refreshGlobalProducts()
              throw apiError
            }
          } else {
            // REORDER WITHIN SAME CATEGORY
            const currentFamilies = productStructure[sourceCatId] || []
            const reorderedFamilies = [...currentFamilies]
            const [movedFamily] = reorderedFamilies.splice(source.index, 1)
            reorderedFamilies.splice(destination.index, 0, movedFamily)

            // Optimistic update
            setProductStructure((prev) => ({
              ...prev,
              [sourceCatId]: reorderedFamilies,
            }))

            try {
              await reorderFamilies(sourceCatId, reorderedFamilies)
              await loadProductStructure()
            } catch (apiError) {
              // ROLLBACK
              setProductStructure(originalStructure)
              throw apiError
            }
          }
        }

        // Handle CATEGORY dragging (reorder only)
        else if (type === "CATEGORY") {
          const currentCategories = Object.keys(productStructure)
          const reorderedCategories = [...currentCategories]
          const categoryName = draggableId.replace("category-", "")

          const sourceIndex = currentCategories.indexOf(categoryName)
          if (sourceIndex === -1) {
            console.error("Category not found:", categoryName)
            return
          }

          const [movedCategory] = reorderedCategories.splice(sourceIndex, 1)
          reorderedCategories.splice(destination.index, 0, movedCategory)

          // Optimistic update
          const reorderedStructure = {}
          reorderedCategories.forEach((cat) => {
            reorderedStructure[cat] = productStructure[cat]
          })
          setProductStructure(reorderedStructure)

          try {
            await reorderCategories(reorderedCategories)
            await loadProductStructure()
          } catch (apiError) {
            // ROLLBACK
            setProductStructure(originalStructure)
            throw apiError
          }
        }
      } catch (error) {
        console.error("Drag and drop error:", error)
        alert(`Errore: ${error.message}`)
        // Refresh data to restore consistency
        await loadProductStructure()
        await refreshGlobalProducts()
      } finally {
        setLoading(false)
      }
    },
    [products, setProducts, setLoading, productStructure]
  )

  return (
    <div className="ay-relative">
      {confirmModal.isOpen && (
        <ConfirmationModal
          {...confirmModal}
          onCancel={() => setConfirmModal({ isOpen: false })}
        />
      )}
      {showCategoryForm && (
        <CategoryForm
          category={selectedCategory}
          isEditing={isEditing}
          onSave={handleSaveCategory}
          onCancel={() => setShowCategoryForm(false)}
        />
      )}
      {showFamilyForm && (
        <FamilyForm
          category={selectedCategory}
          family={selectedFamily}
          isEditing={isEditing}
          initialData={familyDataForForm}
          onSave={handleSaveFamily}
          onCancel={() => {
            setShowFamilyForm(false)
            setFamilyDataForForm(null)
          }}
          onProductChange={handleViewProduct} // NUOVO: Passa la funzione per caricare prodotti
          currentSelectedProduct={null} // TODO: Potresti voler tracciare il prodotto corrente
          products={products} // NUOVO: Passa la lista dei prodotti per il cross-check
        />
      )}
      {showProductForm && (
        <ProductForm
          product={selectedProduct}
          category={selectedCategory}
          family={selectedFamily}
          isEditing={isEditing}
          familyInitialData={familyDataForForm}
          onSave={handleSaveProduct}
          onCancel={() => {
            setShowProductForm(false)
            setFamilyDataForForm(null)
          }}
        />
      )}
      {showProductsManager && (
        <ProductsManagerModal
          products={productsToManage}
          context={manageContext}
          onClose={() => setShowProductsManager(false)}
          onProductEdit={handleEditProduct}
          onProductDelete={handleConfirmDeleteProduct}
          onProductView={handleViewProduct} // AGGIORNATO: Usa la nuova funzione
          onProductsUpdated={handleProductsUpdated}
        />
      )}

      <div className="ay-flex ay-justify-between ay-items-center ay-mb-6">
        <h2 className="ay-text-2xl ay-font-bold">Product Management</h2>
        <button
          onClick={handleAddCategory}
          className="ay-px-4 ay-py-2 ay-bg-green-500 ay-text-white ay-rounded ay-flex ay-items-center ay-gap-2"
        >
          <FiPlus /> Add Category
        </button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="categories" type="CATEGORY">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="ay-space-y-4"
            >
              {Object.keys(productStructure).map((cat, catIndex) => (
                <Draggable
                  key={cat}
                  draggableId={`category-${cat}`}
                  index={catIndex}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className="ay-border ay-rounded-lg ay-overflow-hidden"
                    >
                      <div
                        className="ay-bg-gray-100 ay-p-4 ay-flex ay-justify-between ay-items-center ay-cursor-pointer"
                        onClick={() => toggleCategory(cat)}
                      >
                        <div className="ay-flex ay-items-center ay-gap-2 ay-font-medium ay-text-lg">
                          {expandedCategories[cat] ? (
                            <FiChevronDown />
                          ) : (
                            <FiChevronRight />
                          )}
                          <span>{cat}</span>
                        </div>
                        <div className="ay-flex ay-gap-2 ay-items-center">
                          <div
                            {...provided.dragHandleProps}
                            className="ay-cursor-move ay-mr-2"
                          >
                            <FiMove />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleAddFamily(cat)
                            }}
                            className="ay-p-2 ay-bg-blue-500 ay-text-white ay-rounded ay-text-sm"
                          >
                            <FiPlus />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditCategory(cat)
                            }}
                            className="ay-p-2 ay-bg-yellow-500 ay-text-white ay-rounded ay-text-sm"
                          >
                            <FiEdit />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDuplicateCategory(cat)
                            }}
                            className="ay-p-2 ay-bg-green-600 ay-text-white ay-rounded ay-text-sm"
                            title="Duplicate Category"
                          >
                            <FiCopy />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleConfirmDeleteCategory(cat)
                            }}
                            className="ay-p-2 ay-bg-red-500 ay-text-white ay-rounded ay-text-sm"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                      {expandedCategories[cat] && (
                        <Droppable
                          droppableId={`category-${encodeURIComponent(cat)}`}
                          type="FAMILY"
                          // Permetti drop da qualsiasi categoria
                          isDropDisabled={false}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={`ay-pl-8 ay-py-2 ay-min-h-8 ay-transition-colors ${
                                snapshot.isDraggingOver
                                  ? "ay-bg-blue-50 ay-border-2 ay-border-dashed ay-border-blue-300 ay-rounded"
                                  : ""
                              }`}
                              style={{
                                // Evidenzia quando si sta droppando una famiglia da un'altra categoria
                                backgroundColor: snapshot.isDraggingOver
                                  ? "#eff6ff"
                                  : "transparent",
                              }}
                            >
                              {productStructure[cat].map((fam, famIndex) => (
                                <Draggable
                                  key={`${cat}-${fam}`}
                                  draggableId={`family-${cat}-${fam}`}
                                  index={famIndex}
                                >
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className="ay-border ay-rounded-lg ay-overflow-hidden ay-mb-3"
                                    >
                                      <div
                                        className="ay-bg-gray-50 ay-p-3 ay-flex ay-justify-between ay-items-center ay-cursor-pointer"
                                        onClick={() => toggleFamily(cat, fam)}
                                      >
                                        <div className="ay-flex ay-items-center ay-gap-2 ay-font-medium">
                                          {expandedFamilies[`${cat}-${fam}`] ? (
                                            <FiChevronDown />
                                          ) : (
                                            <FiChevronRight />
                                          )}
                                          <span>{fam}</span>
                                          <span className="ay-ml-2 ay-text-xs ay-bg-blue-100 ay-text-blue-800 ay-rounded-full ay-px-2">
                                            {
                                              getProductsForFamily(cat, fam)
                                                .length
                                            }{" "}
                                            products
                                          </span>
                                        </div>
                                        <div className="ay-flex ay-gap-2 ay-items-center">
                                          <div
                                            {...provided.dragHandleProps}
                                            className="ay-cursor-move ay-mr-2"
                                          >
                                            <FiMove />
                                          </div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleOpenProductsManager(
                                                cat,
                                                fam
                                              )
                                            }}
                                            className="ay-p-2 ay-bg-indigo-500 ay-text-white ay-rounded ay-text-sm"
                                          >
                                            <FiGrid />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleAddProduct(cat, fam)
                                            }}
                                            className="ay-p-2 ay-bg-green-500 ay-text-white ay-rounded ay-text-sm"
                                          >
                                            <FiPlus />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleEditFamily(cat, fam)
                                            }}
                                            className="ay-p-2 ay-bg-yellow-500 ay-text-white ay-rounded ay-text-sm"
                                          >
                                            <FiEdit />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleDuplicateFamily(cat, fam)
                                            }}
                                            className="ay-p-2 ay-bg-green-600 ay-text-white ay-rounded ay-text-sm"
                                            title="Duplicate Family"
                                          >
                                            <FiCopy />
                                          </button>

                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleConfirmDeleteFamily(
                                                cat,
                                                fam
                                              )
                                            }}
                                            className="ay-p-2 ay-bg-red-500 ay-text-white ay-rounded ay-text-sm"
                                          >
                                            <FiTrash2 />
                                          </button>
                                        </div>
                                      </div>
                                      {expandedFamilies[`${cat}-${fam}`] && (
                                        <Droppable
                                          droppableId={`family-${encodeURIComponent(
                                            cat
                                          )}|${encodeURIComponent(fam)}`}
                                          type="PRODUCT"
                                        >
                                          {(provided) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.droppableProps}
                                              className="ay-p-3"
                                            >
                                              <table className="ay-min-w-full ay-divide-y ay-divide-gray-200">
                                                <thead className="ay-bg-gray-50">
                                                  <tr>
                                                    <th className="ay-w-10"></th>
                                                    <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500">
                                                      Name
                                                    </th>
                                                    <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500">
                                                      Emersya Code
                                                    </th>
                                                    <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500">
                                                      OnPage Code
                                                    </th>
                                                    <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500">
                                                      Price
                                                    </th>
                                                    <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500">
                                                      Actions
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody className="ay-bg-white ay-divide-y ay-divide-gray-200">
                                                  {getProductsForFamily(
                                                    cat,
                                                    fam
                                                  ).map(
                                                    (product, prodIndex) => (
                                                      <Draggable
                                                        key={product.code}
                                                        draggableId={`product-${product.code}`}
                                                        index={prodIndex}
                                                      >
                                                        {(provided) => (
                                                          <tr
                                                            ref={
                                                              provided.innerRef
                                                            }
                                                            {...provided.draggableProps}
                                                          >
                                                            <td className="ay-w-10 ay-px-2">
                                                              <div
                                                                {...provided.dragHandleProps}
                                                                className="ay-cursor-move"
                                                              >
                                                                <FiMove />
                                                              </div>
                                                            </td>
                                                            <td className="ay-px-6 ay-py-4">
                                                              <div className="ay-text-sm ay-font-medium">
                                                                {getDisplayName(
                                                                  product
                                                                )}
                                                              </div>
                                                            </td>
                                                            <td className="ay-px-6 ay-py-4">
                                                              {product.code}
                                                            </td>
                                                            <td className="ay-px-6 ay-py-4">
                                                              {
                                                                product.onpage_code
                                                              }
                                                            </td>
                                                            <td className="ay-px-6 ay-py-4">
                                                              <FiDollarSign className="ay-inline ay-mr-1" />
                                                              {product.price}
                                                            </td>
                                                            <td className="ay-px-6 ay-py-4 ay-gap-1 ay-grid ay-grid-cols-2">
                                                              <button
                                                                onClick={() =>
                                                                  handleViewProduct(
                                                                    product
                                                                  )
                                                                }
                                                                className="ay-p-1.5 ay-bg-blue-500 ay-text-white ay-rounded ay-text-xs"
                                                                title="Carica nel viewer"
                                                              >
                                                                <FiEye />
                                                              </button>
                                                              <button
                                                                onClick={() =>
                                                                  handleEditProduct(
                                                                    product
                                                                  )
                                                                }
                                                                className="ay-p-1.5 ay-bg-yellow-500 ay-text-white ay-rounded ay-text-xs"
                                                              >
                                                                <FiEdit />
                                                              </button>
                                                              <button
                                                                onClick={() =>
                                                                  handleDuplicateProduct(
                                                                    product
                                                                  )
                                                                }
                                                                className="ay-p-1.5 ay-bg-green-600 ay-text-white ay-rounded ay-text-xs"
                                                                title="Duplicate Product"
                                                              >
                                                                <FiCopy />
                                                              </button>
                                                              <button
                                                                onClick={() =>
                                                                  handleConfirmDeleteProduct(
                                                                    product
                                                                  )
                                                                }
                                                                className="ay-p-1.5 ay-bg-red-500 ay-text-white ay-rounded ay-text-xs"
                                                              >
                                                                <FiTrash2 />
                                                              </button>
                                                            </td>
                                                          </tr>
                                                        )}
                                                      </Draggable>
                                                    )
                                                  )}
                                                  {provided.placeholder}
                                                </tbody>
                                              </table>
                                            </div>
                                          )}
                                        </Droppable>
                                      )}
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

export default ProductsTab
